import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

export interface ItineraryWizardState {
  orderId: string | null;
  idempotencyKey: string | null;
  step: number;
  selectedPackageId: string | null;
  bookingDetails: Record<string, any> | null;
  paymentRequest: Record<string, any> | null;
  finalItinerary: Record<string, any> | null;
  pageStatus: string;
  errorMessage: string;
}

export const itineraryWizardState: ItineraryWizardState = {
  orderId: null,
  idempotencyKey: null,
  step: 1,
  selectedPackageId: null,
  bookingDetails: null,
  paymentRequest: null,
  finalItinerary: null,
  pageStatus: 'idle',
  errorMessage: '',
};

export const itineraryWizardSlice = createSlice({
  name: 'itineraryWizard',
  initialState: itineraryWizardState,
  reducers: {
    startWizard: (draft, { payload }: PayloadAction<{ idempotencyKey: string; packageId?: string }>) => {
      draft.orderId = null;
      draft.idempotencyKey = payload.idempotencyKey;
      draft.selectedPackageId = payload.packageId ?? null;
      draft.step = 1;
      draft.bookingDetails = null;
    },
    setWizardStep: (draft, { payload }: PayloadAction<number>) => {
      draft.step = payload;
    },
    setSelectedPackageId: (draft, { payload }: PayloadAction<string | null>) => {
      draft.selectedPackageId = payload;
    },
    setBookingDetails: (draft, { payload }: PayloadAction<Record<string, any> | null>) => {
      draft.bookingDetails = payload;
    },
    requestItineraryPayment: (draft) => {
      draft.pageStatus = 'running';
      draft.errorMessage = '';
      draft.paymentRequest = null;
    },
    requestItineraryPaymentSuccess: (draft, { payload }: PayloadAction<Record<string, any>>) => {
      draft.pageStatus = 'success';
      draft.errorMessage = '';
      draft.paymentRequest = payload;
      draft.orderId = payload.payment?.orderId || payload.orderId || null;
      draft.step = 3;
    },
    requestItineraryPaymentFailure: (draft, { payload }: PayloadAction<{ errorMessage?: string }>) => {
      draft.pageStatus = 'error';
      draft.errorMessage = payload.errorMessage || 'Failed to request payment';
      draft.paymentRequest = null;
    },
    getItineraryOrder: (draft, { payload }: PayloadAction<{ orderId: string }>) => {
      draft.orderId = payload.orderId;
      draft.step = 4;
      draft.pageStatus = 'running';
      draft.errorMessage = '';
      draft.finalItinerary = null;
    },
    getItineraryOrderSuccess: (draft, { payload }: PayloadAction<Record<string, any>>) => {
      draft.step = 4;
      draft.pageStatus = 'success';
      draft.errorMessage = '';
      draft.finalItinerary = payload;
    },
    getItineraryOrderFailure: (draft, { payload }: PayloadAction<{ errorMessage?: string }>) => {
      draft.step = 4;
      draft.pageStatus = 'error';
      draft.errorMessage = payload.errorMessage || 'Failed to load itinerary order.';
      draft.finalItinerary = null;
    },
    resetWizard: (draft) => {
      draft.orderId = null;
      draft.idempotencyKey = null;
      draft.step = 1;
      draft.selectedPackageId = null;
      draft.bookingDetails = null;
      draft.paymentRequest = null;
      draft.finalItinerary = null;
      draft.pageStatus = 'idle';
      draft.errorMessage = '';
    },
  },
});

export const {
  startWizard,
  setWizardStep,
  setSelectedPackageId,
  setBookingDetails,
  requestItineraryPayment,
  requestItineraryPaymentSuccess,
  requestItineraryPaymentFailure,
  getItineraryOrder,
  getItineraryOrderSuccess,
  getItineraryOrderFailure,
  resetWizard,
} = itineraryWizardSlice.actions;

export default itineraryWizardSlice.reducer;
